import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { TooltipContentProps, TooltipProps as RadixTooltipProps } from '@radix-ui/react-tooltip';
import classNames from 'classnames';
import { colors } from '../../style';

const slideUpAndFade = keyframes`
  0% {
    opacity: 0;
    transform: translateY(2px);
  }


  100% {
    opacity: 1;
    transform: translateY(0);
  }

`;

const slideRightAndFade = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-2px);
  }


  100% {
    opacity: 1;
    transform: translateX(0);
  }

`;

const slideDownAndFade = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-2px);
  }


  100% {
    opacity: 1;
    transform: translateY(0)
  }

`;

const slideLeftAndFade = keyframes`
  0% {
    opacity: 0;
    transform: translateX(2px);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const variants = {
  default: css`
    border-radius: 4px;
    padding: 6px 8px;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.25rem;
    color: white;
    background: ${colors.gray900};
    box-shadow:
      hsl(206 22% 7% / 35%) 0 10px 38px -10px,
      hsl(206 22% 7% / 20%) 0px 10px 20px -15px;
  `,
  transparent: css`
    background: transparent;
    margin: 0;
  `,
};

const sizes = {
  auto: {
    maxWidth: 'auto',
  },
  md: {
    maxWidth: '260px',
  },
};

const StyledContent = styled(TooltipPrimitive.Content).withConfig({
  shouldForwardProp: prop => !['variant', 'size', 'minContentWidth', 'textAlign'].includes(prop),
})<{
  variant: keyof typeof variants;
  size: keyof typeof sizes;
  minContentWidth: boolean;
  textAlign?: 'center';
}>`
  user-select: none;
  ${props =>
    props.textAlign &&
    css`
      text-align: ${props.textAlign};
    `};
  ${props => variants[props.variant || 'default']};
  z-index: 99999;
  min-width: ${props => (props.minContentWidth ? 'min-content' : undefined)};
  max-width: ${props => sizes[props.size]?.maxWidth};
  white-space: pre-line; /* This makes \n works as carrier return */
  word-break: break-word; /* Long unbroken tokens wrap onto multiple lines instead of clipping */

  @media (prefers-reduced-motion: no-preference) {
    animation-duration: 400ms;
    animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
    will-change: transform, opacity;
    &[data-state='delayed-open'] {
      &[data-side='top'] {
        animation-name: ${slideDownAndFade};
      }

      &[data-side='right'] {
        animation-name: ${slideLeftAndFade};
      }

      &[data-side='bottom'] {
        animation-name: ${slideUpAndFade};
      }

      &[data-side='left'] {
        animation-name: ${slideRightAndFade};
      }
    }
  }
`;

const StyledArrow = styled(TooltipPrimitive.Arrow)`
  fill: black;
`;

// Exports
const Provider = TooltipPrimitive.Provider;
const Root = TooltipPrimitive.Root;
const TooltipTrigger = styled(TooltipPrimitive.Trigger)<{
  cursor?: TooltipProps['cursor'];
}>`
  cursor: ${props => props.cursor};

  .component-wrapper {
    &.full-width {
      width: 100%;
    }

    &.block {
      display: block;
    }

    &.inline-block {
      display: inline-block;
    }
  }
`;

export interface TooltipProps extends RadixTooltipProps {
  text?: React.ReactNode;
  title?: React.ReactNode;
  markup?: React.ReactNode;
  variant?: keyof typeof variants;
  withArrow?: boolean;
  textAlign?: 'center';
  align?: TooltipContentProps['align'];
  side?: TooltipContentProps['side'];
  disabled?: boolean;
  block?: boolean;
  fullWidth?: boolean;
  inlineBlock?: boolean;
  containerComponent?: React.ElementType;
  size?: keyof typeof sizes;
  className?: string;
  cursor?: 'default';

  openOnClick?: boolean;

  /**
   * Match tooltip's `min-width` with the content.
   * Useful for tooltips that contain long strings without breaks.
   */
  minContentWidth?: boolean | undefined;
}

const Tooltip = ({
  text,
  title,
  markup,
  children,
  variant = 'default',
  withArrow = false,
  textAlign,
  align,
  side,
  disabled,
  block = true,
  fullWidth,
  inlineBlock,
  size = 'md',
  onOpenChange,
  className,
  delayDuration = 50,
  cursor,
  containerComponent: Component,
  minContentWidth = false,
}: TooltipProps): React.JSX.Element => {
  const tooltipContent = text || title || markup;

  if (!tooltipContent || disabled) {
    return <>{children}</>;
  }

  const tooltipTrigger = Component ? (
    <Component
      className={classNames('component-wrapper', {
        'full-width': fullWidth,
        block,
        'inline-block': inlineBlock,
        className,
      })}
    >
      {children}
    </Component>
  ) : (
    children
  );

  return (
    <Provider delayDuration={delayDuration}>
      <Root
        onOpenChange={open => {
          onOpenChange?.(open);
        }}
      >
        <TooltipTrigger cursor={cursor} className={className} asChild>
          {tooltipTrigger}
        </TooltipTrigger>

        <TooltipPrimitive.Portal>
          <StyledContent
            size={size}
            textAlign={textAlign}
            align={align}
            side={side}
            variant={variant}
            sideOffset={5}
            minContentWidth={minContentWidth}
          >
            {tooltipContent}
            {withArrow && <StyledArrow />}
          </StyledContent>
        </TooltipPrimitive.Portal>
      </Root>
    </Provider>
  );
};

export default Tooltip;
